import type { RunBatchActionInput } from '@/gql/graphql'
import { BatchActionType, BatchItemStatus } from '@/gql/graphql'

type SelectedRows = Record<string, any>

const groupRowsByBatchId = (selectedRows: SelectedRows): Record<string, string[]> => {
  const byBatch: Record<string, string[]> = {}
  Object.values(selectedRows).forEach((row: any) => {
    const batchId = row?.batches?.data?.[0]?.id
    if (!batchId) return
    if (!byBatch[batchId]) byBatch[batchId] = []
    byBatch[batchId].push(row.id)
  })
  return byBatch
}

export const buildBatchActionInputs = ({
  selectedRows,
  excludedIds = [],
  isFullScope,
  selectedBatchIds,
  action,
  deleteIfEmptied,
  status,
}: {
  selectedRows: SelectedRows
  excludedIds?: (string | number)[]
  isFullScope: boolean
  selectedBatchIds: (string | number)[]
  action: BatchActionType
  deleteIfEmptied: boolean
  status?: BatchItemStatus
}): RunBatchActionInput[] => {
  let inputs: RunBatchActionInput[]
  if (isFullScope) {
    inputs = selectedBatchIds.map((batch) => ({
      batch: String(batch),
      action,
      scope: {
        ...(excludedIds.length ? { excludeIds: excludedIds.map((id) => String(id)) } : {}),
        ...(status ? { status } : {}),
      },
      deleteIfEmptied,
    }))
  } else {
    const byBatch = groupRowsByBatchId(selectedRows)
    inputs = Object.entries(byBatch).map(([batch, ids]) => ({
      batch: String(batch),
      action,
      scope: { ids: ids.map((id) => String(id)) },
      deleteIfEmptied,
    }))
  }
  return inputs
}

export const downloadFromUrls = async (urls: (string | undefined | null)[]) => {
  for (const url of urls) {
    if (url) {
      const a = document.createElement('a')
      a.href = url
      a.rel = 'noopener'
      a.download = ''
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      // small delay between multiple downloads
      await new Promise<void>((resolve) => {
        setTimeout(resolve, 150)
      })
    }
  }
}

export const getBatchIdFromRow = (row: any): string | null =>
  row?.batchId ?? row?.batches?.data?.[0]?.id ?? null
